import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, fmtKLDateTime } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/* KL wall-clock value for <input type="datetime-local"> */
const toKLInputValue = (iso) => {
  const d = new Date(iso);
  const fmt = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Kuala_Lumpur",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
  return fmt.format(d).replace(" ", "T");
};

/* Supervisor/admin punch override: correct the clock time and/or location.
   The backend preserves the original time, recomputes late/early minutes and
   writes a full audit entry. */
export const PunchEditDialog = ({ open, onOpenChange, punch }) => {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const [time, setTime] = useState("");
  const [locationId, setLocationId] = useState("keep");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: locData } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
    enabled: open,
  });
  const locations = locData?.items || [];

  useEffect(() => {
    if (open && punch) {
      setTime(toKLInputValue(punch.serverTimestamp));
      setLocationId("keep");
      setNote("");
    }
  }, [open, punch]);

  const originalTime = toKLInputValue(punch?.serverTimestamp || new Date().toISOString());
  const changedTime = time && time !== originalTime;
  const changedLocation = locationId !== "keep";

  const save = async () => {
    if (!changedTime && !changedLocation) {
      onOpenChange(false);
      return;
    }
    setBusy(true);
    try {
      const body = {};
      if (changedTime) body.serverTimestamp = time; // naive = KL local, backend converts
      if (changedLocation) body.locationId = locationId;
      if (note.trim()) body.note = note.trim();
      await api.patch(`/punches/${punch.id}/edit`, body);
      toast.success(t("punchUpdated"));
      queryClient.invalidateQueries();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="punch-edit-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("editPunch")}</DialogTitle>
          <DialogDescription>
            {punch?.userName} · {punch?.punchType === "clock_in" ? t("clockInLabel") : t("clockOutLabel")} ·{" "}
            <span className="font-mono tabular-nums">{punch && fmtKLDateTime(punch.serverTimestamp)}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{t("correctedTime")}</label>
            <Input
              type="datetime-local"
              data-testid="punch-edit-time-input"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
            {punch?.originalTimestamp && (
              <p className="text-[11px] text-muted-foreground">
                {t("originalTime")}: {fmtKLDateTime(punch.originalTimestamp)}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{t("locationLabel")}</label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger data-testid="punch-edit-location-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="keep">
                  {punch?.siteName ? `${t("keepLocation")} (${punch.siteName})` : t("keepLocation")}
                </SelectItem>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{t("editNoteLabel")}</label>
            <Textarea
              data-testid="punch-edit-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder={t("editNotePlaceholder")}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button
            data-testid="punch-edit-save-button"
            onClick={save}
            disabled={busy || (!changedTime && !changedLocation)}
          >
            {t("saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
